import SearchEmpty from "@images/empty/session_empty.svg"
import { CrossIcon } from "@images/svgIcons/crossIcon"
import { PencilSolidIcon } from "@images/svgIcons/essentials/pencilSolidIcon"
import { TrashSolidIcon } from "@images/svgIcons/essentials/trashSolidIcon"
import { word } from "@modules/core/utils/i18n"
import { ExploreProfileItemEntity } from "@modules/explore/entities/exploreEntity"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import { useExploreSearchContents } from "@modules/explore/hooks/useExplore"
import { InputContainer } from "@modules/formik/components/inputContainer"
import useDebounce from "@modules/hooks/useDebounce"
import { UserCell } from "@modules/members/components/userCell"
import { UserCellSkeleton } from "@modules/members/components/userCellSkeleton"
import { User } from "@modules/profile/profileTypes"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { FieldValueChange } from "@src/typings/formik"
import { Search, SearchIcon } from "lucide-react"
import { InputText } from "primereact/inputtext"
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useTheme } from "styled-components"

type SearchUserInputProps<K> = {
  label?: string
  name: string
  textDescription?: string
  searchDescriptionText?: string
  isInvalid: boolean
  isRequiredInput: boolean
  setFieldValue: FieldValueChange<K>
  user?: User | null
  classNameDiv?: string
  // getFieldProps: (nameOrOptions: T) => FieldInputProps<any>
  error?: React.JSX.Element
} & React.HTMLAttributes<HTMLDivElement>

type SearchUserModalRef = {
  open: () => void
}

export function SearchUserInput<K>({
  label,
  name,
  isRequiredInput,
  isInvalid,
  setFieldValue,
  // getFieldProps,
  textDescription,
  searchDescriptionText,
  error,
  user,
  classNameDiv,
  ...props
}: SearchUserInputProps<K>): React.JSX.Element {
  const theme = useTheme()
  const searchUserModal = useRef<SearchUserModalRef>(null)
  const [userSelected, setUserSelected] = useState<ExploreProfileItemEntity | User | null>(user ?? null)
  const defaultClassNameDiv = "flex flex-column gap-3 bg-neutralColor-50 border-round-xl p-4"

  const handleTrash = () => {
    setUserSelected(null)
    setFieldValue(name, null)
  }

  const handleEdit = () => {
    searchUserModal.current?.open()
  }

  useEffect(() => {
    if (userSelected) {
      setFieldValue(name, userSelected.id)
    }
  }, [userSelected])

  return (
    <>
      <InputContainer
        isRequiredInput={isRequiredInput}
        label={label}
        name={name}
        isInvalid={isInvalid}
        error={error}
        {...props}
      >
        <div className={classNameDiv ?? defaultClassNameDiv}>
          {textDescription && (
            <HUText fontStyle="BM" color={theme.neutralColor700}>
              {textDescription}
            </HUText>
          )}
          {userSelected ? (
            <div className="flex justify-content-between">
              <UserCell
                member={{
                  avatar: userSelected.avatar,
                  firstname: userSelected.firstname,
                  lastname: userSelected.lastname,
                }}
                subtitle={userSelected.username === "" ? "" : "@" + userSelected.username}
                avatarSize={55}
              />
              <div className="flex align-items-center justify-content-center gap-3">
                <PencilSolidIcon
                  color={theme.mainWhite}
                  width={24}
                  height={24}
                  className="cursor-pointer"
                  onClick={() => handleEdit()}
                />
                <TrashSolidIcon
                  color={theme.mainWhite}
                  width={24}
                  height={24}
                  className="cursor-pointer"
                  onClick={() => handleTrash()}
                />
              </div>
            </div>
          ) : (
            <HUButton
              type="Right_Icon"
              size="M"
              colorType="Quaternary"
              text={word("searchUserInput.modal.searchText")}
              icon={{ iconView: <Search size={16} color={theme.mainWhite} /> }}
              onClick={() => searchUserModal.current?.open()}
              className="w-full"
            />
          )}
        </div>
      </InputContainer>
      <SearchUserModal
        ref={searchUserModal}
        setUserSelected={setUserSelected}
        searchDescriptionText={searchDescriptionText}
      />
    </>
  )
}

function SearchUserModal({
  setUserSelected,
  searchDescriptionText,
  ref,
}: {
  setUserSelected: (user: ExploreProfileItemEntity) => void
  searchDescriptionText?: string
  ref: React.Ref<SearchUserModalRef>
}) {
  const theme = useTheme()
  const [showModal, setShowModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const {
    data: { content: result, totalSize },
    fetchNextPage,
    setSearchTerms: setSearchContentTerms,
    isLoading,
  } = useExploreSearchContents(ExploreFilter.PROFILE)

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  const clearSearch = () => {
    setSearchTerm("")
  }

  const handleClick = (profile: ExploreProfileItemEntity) => {
    setUserSelected(profile)
    setShowModal(false)
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  useEffect(() => {
    if (debouncedSearchTerm) {
      setSearchContentTerms(debouncedSearchTerm)
    }
  }, [debouncedSearchTerm])

  return (
    <HUDialog visible={showModal} header={""} onHide={onHide} style={{ width: "512px", height: 650 }}>
      <div className="flex flex-column w-full h-full">
        {/* TITLE */}
        <div className="flex flex-column flex-wrap justify-content-center align-content-center text-center	">
          <HUText fontStyle="TL" color={theme.mainWhite}>
            {word("searchUserInput.modal.title")}
          </HUText>
        </div>
        {/* SEARCH */}
        <div className="flex w-full mt-5">
          <div
            className=" flex justify-content-between w-full h-3rem rounded-full border-1 border-neutral-300"
            onFocus={(e) => {
              e.currentTarget.classList.remove("border-neutral-300")
              e.currentTarget.classList.add("border-electricPurple-400")
            }}
            onBlur={(e) => {
              e.currentTarget.classList.remove("border-electricPurple-400")
              e.currentTarget.classList.add("border-neutral-300")
            }}
          >
            <div className="flex flex-grow-1 align-items-center">
              <div className="flex align-items-center justify-content-center w-3rem h-full ">
                <SearchIcon color={theme.neutralColor700} width={16} height={16} />
              </div>
              <InputText
                className="flex flex-grow-1 h-full p-0 m-0 border-none outline-none"
                placeholder={word("searchUserInput.modal.placeholder")}
                value={searchTerm}
                style={{ borderRadius: "0px" }}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
              />
            </div>
            {searchTerm && (
              <button onClick={clearSearch} className="flex align-items-center justify-content-center w-3rem h-full ">
                <CrossIcon color={theme.neutralColor700} width={14} height={14} />
              </button>
            )}
          </div>
        </div>
        {/* SEARCH RESULTS */}
        <div className="flex flex-column w-full h-full">
          {searchTerm !== "" && ((result && result.length > 0) || isLoading) ? (
            <InfiniteScroll
              next={fetchNextPage}
              dataLength={result.length}
              hasMore={result.length < (totalSize || 0)}
              loader={templateUserCellSkeleton()}
              className="flex flex-column gap-2 mt-4 mb-4"
            >
              {result.map(
                (person) =>
                  person.profile && (
                    <UserCell
                      key={person.profile.id}
                      member={{
                        avatar: person.profile.avatar,
                        username: person.profile.username,
                        firstname: person.profile.firstname,
                        lastname: person.profile.lastname,
                      }}
                      subtitle={person.profile.username === "" ? "" : "@" + person.profile.username}
                      avatarSize={55}
                      className="flex gap-2 cursor-pointer"
                      onClick={() => person.profile && handleClick(person.profile)}
                    />
                  ),
              )}
            </InfiniteScroll>
          ) : (
            <div className="flex flex-column w-full h-full gap-4 align-items-center justify-content-center ">
              <HUText fontStyle="LL" color={theme.mainWhite}>
                {searchTerm !== "" ? word("searchUserInput.modal.emptyViewtext") : searchDescriptionText}
              </HUText>
              <div
                className="flex w-full h-10rem bg-contain bg-no-repeat bg-center"
                style={{ backgroundImage: `url('${SearchEmpty}')` }}
              ></div>
            </div>
          )}
        </div>
      </div>
    </HUDialog>
  )
}

const templateUserCellSkeleton = () => {
  return (
    <div className="flex flex-column">
      <UserCellSkeleton subtitle={true} avatarSize={60} />
      <UserCellSkeleton subtitle={true} avatarSize={60} />
      <UserCellSkeleton subtitle={true} avatarSize={60} />
      <UserCellSkeleton subtitle={true} avatarSize={60} />
      <UserCellSkeleton subtitle={true} avatarSize={60} />
      <UserCellSkeleton subtitle={true} avatarSize={60} />
    </div>
  )
}
